/**
 * What the brain is not sure about, and the notes behind each number.
 *
 * WHY IT IS ON THE DECK. Every reliability problem this vault has had reached him through me: 262
 * organisations claiming a relationship nobody on their side ever wrote, 825 open commitments with
 * no date, twenty spellings of five languages. Each was a script I ran once, and each went stale the
 * moment I stopped running it. A brain whose trustworthiness is only visible through an assistant is
 * one he has to keep auditing through an assistant.
 *
 * WHY EVERY ROW OPENS. This board is full of counts, and it has shown him wrong ones: three copies
 * of his own company in the graph, and a due-date figure I repeated for a week that was out by a
 * factor of ten. An unopenable number is an unverifiable number. One tap turns each row from
 * something he has to believe into something he can check, which is the difference between an
 * instrument and a decoration.
 *
 * The list expands INLINE rather than in a modal. A modal would cover the rest of the panel, and the
 * question "is this count right" is usually answered by two or three titles, not by a full-screen
 * table. It also keeps the whole thing usable with one thumb on a phone.
 */

import { useState, type JSX } from 'react';
import type { DashboardHealthRow } from '@rbv/shared';
import { Icon } from './Icon.js';
import { queryNotes, type QueriedNote } from './api.js';

export function HealthPanel({ rows }: { rows: DashboardHealthRow[] }): JSX.Element | null {
  // An older server omits the field entirely. Rendering an empty panel would imply a clean bill of
  // health that was never actually measured.
  if (rows.length === 0) return null;

  const flagged = rows.filter((r) => r.count > 0).length;

  return (
    <section className="deck-panel" data-hue="know">
      <header className="deck-panel-head">
        <Icon name="holdings" size={16} className="deck-panel-icon" />
        <h2 className="deck-panel-title">What the brain is unsure of</h2>
        <span className="deck-panel-count">{flagged === 0 ? 'clear' : `${flagged}`}</span>
      </header>
      <div className="deck-panel-body">
        {/*
          Stated, not left to be inferred. A row of zeroes only reads as good news once you know what
          it would have said otherwise, and he will read this panel for the first time on a day when
          it is full.
        */}
        <p className="deck-panel-note">
          {flagged === 0
            ? 'Nothing unsupported, unresolved or undated. These are properties of the notes, not work items.'
            : 'Properties of the notes themselves, not work items. Every figure opens into the notes it counted.'}
        </p>
        <ul className="mo-health">
          {rows.map((row) => (
            <HealthLine key={row.key} row={row} />
          ))}
        </ul>
      </div>
    </section>
  );
}

function HealthLine({ row }: { row: DashboardHealthRow }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [notes, setNotes] = useState<QueriedNote[] | null>(null);
  const [error, setError] = useState('');
  const [truncated, setTruncated] = useState(false);
  const openable = row.query !== undefined && row.count > 0;

  const toggle = async (): Promise<void> => {
    if (open) {
      setOpen(false);
      return;
    }
    setOpen(true);
    // Fetched once and kept: re-fetching on every toggle would make an idle click cost a vault walk.
    if (notes !== null || row.query === undefined) return;
    const res = await queryNotes(row.query);
    if (res.ok) {
      setNotes(res.rows);
      setTruncated(res.truncated);
    } else {
      setError(res.error);
    }
  };

  return (
    <li className="mo-health-row" data-tier={row.tier} data-flagged={row.count > 0}>
      <div className="mo-health-line">
        <span className="mo-health-label">{row.label}</span>
        {/*
          The denominator is not decoration. "262" is a number he has to trust; "262 of 470" is a
          claim he can check, and that is the whole reason this panel is allowed to show numbers.
        */}
        <span className="mo-health-count">
          {row.count}
          <span className="mo-health-of"> of {row.scanned}</span>
        </span>
      </div>
      <p className="mo-health-note">{row.note}</p>
      {openable && (
        <button type="button" className="deck-more" aria-expanded={open} onClick={() => void toggle()}>
          {open ? 'Hide the notes' : `Show the ${row.count}`}
        </button>
      )}
      {open && error !== '' && <p className="mo-health-error">{error}</p>}
      {open && error === '' && (
        <ul className="mo-health-notes">
          {notes === null ? (
            <li className="mo-health-loading">Counting.</li>
          ) : (
            notes.map((n) => (
              <li key={n.path}>
                {/*
                  Straight into the note in the editor. The point of opening a count is to read one
                  of the notes behind it, and stopping at a list of titles would leave him searching
                  the notes app for a name he can already see.
                */}
                <a className="mo-health-link" href={`/#${encodeURI(n.path)}`}>
                  {n.title}
                </a>
              </li>
            ))
          )}
          {truncated && <li className="mo-health-more">Showing the first 50.</li>}
        </ul>
      )}
    </li>
  );
}
